import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Save, Undo2, Copy, CalendarDays, Wand2, Check, Clock, ChevronDown, ChevronUp } from "lucide-react";
import AlertModal from "../../../../components/AlertModal";
import ReusableDropdown from "../../../../components/ReusableDropdown";
import DatePicker from "../../../../components/DatePicker";
import CellSelect from "./CellSelect";
import { WEEKDAY_SHORT } from "./shiftControlText";

// ── calendar-day helpers (plain "YYYY-MM-DD" keys, UTC arithmetic) ─────────
const keyToDate = (key) => new Date(`${key}T00:00:00Z`);
const dateToKey = (d) => d.toISOString().slice(0, 10);
const addDays = (key, n) => {
  const d = keyToDate(key);
  d.setUTCDate(d.getUTCDate() + n);
  return dateToKey(d);
};
const dowOf = (key) => keyToDate(key).getUTCDay();
const todayKey = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Africa/Cairo" }).format(new Date());

const rangeFor = (mode, anchor) => {
  if (mode === "month") {
    const d = keyToDate(anchor);
    const first = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
    const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
    return { from: dateToKey(first), to: dateToKey(last) };
  }
  const from = addDays(anchor, -dowOf(anchor)); // weeks start on Sunday
  return { from, to: addDays(from, 6) };
};

const daysBetween = (from, to) => {
  const out = [];
  for (let k = from; k <= to; k = addDays(k, 1)) out.push(k);
  return out;
};

const cellKey = (code, date) => `${code}|${date}`;
const toValue = (entry) =>
  !entry || !entry.type ? "" : entry.type === "REST" ? "REST" : `S:${entry.shiftId}`;
const fromValue = (value) =>
  value === "" ? { type: null, shiftId: null }
    : value === "REST" ? { type: "REST", shiftId: null }
      : { type: "SHIFT", shiftId: Number(value.slice(2)) };

export default function ScheduleTab({ styles, t, theme, language, meta, fetchRoster, saveRoster, copyWeek, notifyError, notifySuccess, onDirtyChange, confirmDiscard, footerEl }) {
  const [mode, setMode] = useState("week");
  const [anchor, setAnchor] = useState(todayKey());
  const [company, setCompany] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [edits, setEdits] = useState(() => new Map());
  const [selected, setSelected] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [bulk, setBulk] = useState({ value: "", from: "", to: "", skipWeekends: true });
  const [confirmCopy, setConfirmCopy] = useState(null);
  const [pickerReset, setPickerReset] = useState(0);
  const [legendOpen, setLegendOpen] = useState(false);

  const { from, to } = useMemo(() => rangeFor(mode, anchor), [mode, anchor]);
  const days = useMemo(() => daysBetween(from, to), [from, to]);
  const today = todayKey();
  const dirty = edits.size > 0;
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  const weekdayNames = WEEKDAY_SHORT[language] || WEEKDAY_SHORT.en;

  // Debounce the search box.
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchRoster({ from, to, workingCompany: company, departmentId, search, page, pageSize: 50 });
      setData(result);
      setEdits(new Map());
      setSelected(new Set());
    } catch (err) {
      notifyError(err);
    } finally {
      setLoading(false);
    }
  }, [fetchRoster, from, to, company, departmentId, search, page, notifyError]);

  useEffect(() => { load(); }, [load]);

  // Any navigation that reloads the grid first asks before dropping edits.
  const guard = (fn) => (...args) => {
    if (dirty) confirmDiscard(() => fn(...args));
    else fn(...args);
  };
  // Same guard for the dropdowns: on "cancel" remount them so they show the
  // value that is actually still applied.
  const guardPick = (fn) => (item) => {
    if (dirty) confirmDiscard(() => fn(item), () => setPickerReset((n) => n + 1));
    else fn(item);
  };

  const shiftById = useMemo(() => new Map((data?.shifts || []).map((s) => [s.id, s])), [data]);
  const originals = useMemo(() => {
    const m = new Map();
    for (const e of data?.entries || []) m.set(cellKey(e.infinityCode, e.date), e);
    return m;
  }, [data]);

  const valueAt = (code, date) => {
    const k = cellKey(code, date);
    return edits.has(k) ? toValue(edits.get(k)) : toValue(originals.get(k));
  };

  const setCell = (code, date, value, draft) => {
    const k = cellKey(code, date);
    if (toValue(originals.get(k)) === value) draft.delete(k);
    else draft.set(k, fromValue(value));
  };

  const onCellChange = (code, date) => (e) => {
    const value = e.target.value;
    setEdits((prev) => {
      const next = new Map(prev);
      setCell(code, date, value, next);
      return next;
    });
  };

  const activeShiftsFor = (workingCompany) =>
    (data?.shifts || []).filter((s) => s.isActive && s.workingCompany === workingCompany);

  const departments = useMemo(
    () => (meta.departments || []).filter((d) => !company || d.workingCompany === company),
    [meta.departments, company],
  );

  // ── save / discard ──
  const save = async () => {
    const cells = [...edits.entries()].map(([k, v]) => {
      const [infinityCode, date] = k.split("|");
      return { infinityCode, date, type: v.type, shiftId: v.shiftId };
    });
    setSaving(true);
    try {
      const result = await saveRoster(cells);
      notifySuccess(result?.recalculating ? `${t.saved}. ${t.recalcNote}` : t.saved);
      await load();
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  // ── selection + bulk ──
  const pageCodes = (data?.employees || []).map((e) => e.infinityCode);
  const allSelected = pageCodes.length > 0 && pageCodes.every((c) => selected.has(c));
  const toggle = (code) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(code) ? next.delete(code) : next.add(code);
    return next;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(pageCodes));

  const applyBulk = () => {
    if (!selected.size) return notifyError(new Error(t.selectFirst));
    const bFrom = bulk.from || from;
    const bTo = bulk.to || to;
    const target = fromValue(bulk.value);
    const shift = target.type === "SHIFT" ? shiftById.get(target.shiftId) : null;
    let skipped = 0;
    const next = new Map(edits);
    for (const emp of data.employees) {
      if (!selected.has(emp.infinityCode)) continue;
      if (shift && shift.workingCompany !== emp.workingCompany) { skipped += 1; continue; }
      for (const d of days) {
        if (d < bFrom || d > bTo) continue;
        if (bulk.skipWeekends && emp.weekendDays.includes(dowOf(d))) continue;
        setCell(emp.infinityCode, d, bulk.value, next);
      }
    }
    setEdits(next);
    if (skipped) notifyError(new Error(`${t.otherCompanyShift} (${skipped})`));
  };

  const runCopy = async () => {
    const codes = confirmCopy;
    setConfirmCopy(null);
    try {
      await copyWeek({ infinityCodes: codes, fromWeekStart: addDays(from, -7), toWeekStart: from });
      notifySuccess(t.copied);
      await load();
    } catch (err) {
      notifyError(err);
    }
  };

  const step = (dir) => {
    if (mode === "week") setAnchor(addDays(anchor, dir * 7));
    else {
      const d = keyToDate(anchor);
      setAnchor(dateToKey(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + dir, 1))));
    }
  };

  const rangeLabel = mode === "month"
    ? new Intl.DateTimeFormat(language === "ar" ? "ar-EG" : "en-GB", { month: "long", year: "numeric", timeZone: "UTC" }).format(keyToDate(from))
    : `${from} → ${to}`;

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  const Prev = language === "ar" ? ChevronRight : ChevronLeft;
  const Next = language === "ar" ? ChevronLeft : ChevronRight;

  // English name lives in `sr`; Arabic in `arabicName` (served as nameAr).
  const displayName = (emp) =>
    (language === "ar" ? emp.nameAr || emp.name : emp.name || emp.nameAr) || emp.infinityCode;

  // ── one cell ──
  const renderCell = (emp, date) => {
    const value = valueAt(emp.infinityCode, date);
    const k = cellKey(emp.infinityCode, date);
    const isWeekend = emp.weekendDays.includes(dowOf(date));
    const options = activeShiftsFor(emp.workingCompany);
    const current = value.startsWith("S:") ? shiftById.get(Number(value.slice(2))) : null;
    const currentMissing = current && !options.some((s) => s.id === current.id);

    let bg = null;
    let fg = null;
    if (current) { bg = current.color || "#3B82F6"; fg = "#fff"; }
    else if (value === "REST") { bg = styles.dark ? "rgba(245,158,11,0.25)" : "#FEF3C7"; fg = styles.dark ? "#FCD34D" : "#92400E"; }

    return (
      <td key={date} style={{ ...styles.gridCell, background: date === today ? styles.todayTint : undefined }}>
        <CellSelect
          className="sc-cell"
          value={value}
          options={[
            { value: "", label: value ? t.clear : isWeekend ? t.weekend : "—" },
            ...options.map((s) => ({ value: `S:${s.id}`, label: `${s.name} (${s.startTime}–${s.endTime})`, color: s.color || "#3B82F6" })),
            ...(currentMissing ? [{ value, label: current.name, color: current.color || "#3B82F6", disabled: true }] : []),
            { value: "REST", label: t.restShort, color: "#f59e0b" },
          ]}
          onChange={(v) => onCellChange(emp.infinityCode, date)({ target: { value: v } })}
          theme={theme}
          language={language}
          style={styles.cellSelect({ bg, fg, dirty: edits.has(k), weekend: isWeekend && !value })}
          title={current ? `${current.name} ${current.startTime}–${current.endTime} · ${current.locationName}` : undefined}
          ariaLabel={`${displayName(emp)} ${date}`}
        />
      </td>
    );
  };

  const legendShifts = (data?.shifts || []).filter((s) => s.isActive);
  // Legend stays compact: only the selected company's shifts, first few chips,
  // the rest behind "+N more". Location lives in the chip tooltip.
  const LEGEND_LIMIT = 6;
  const companyLegend = legendShifts.filter((s) => !company || s.workingCompany === company);
  const shownLegend = legendOpen ? companyLegend : companyLegend.slice(0, LEGEND_LIMIT);
  const hiddenLegend = companyLegend.length - LEGEND_LIMIT;

  return (
    <div style={styles.panel}>
      <AlertModal
        open={Boolean(confirmCopy)}
        title={t.copyConfirmTitle}
        message={t.copyConfirm.replace("{n}", String(confirmCopy?.length || 0))}
        variant="warning"
        showCancel
        onConfirm={runCopy}
        onClose={() => setConfirmCopy(null)}
        theme={theme}
        language={language}
      />

      {/* range + filters */}
      <div style={styles.controls}>
        <div style={styles.tabs}>
          <button style={styles.tab(mode === "week")} onClick={guard(() => setMode("week"))}>{t.week}</button>
          <button style={styles.tab(mode === "month")} onClick={guard(() => setMode("month"))}>{t.month}</button>
        </div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <button className="sc-ghost" style={{ ...styles.btn("ghost"), padding: 9 }} onClick={guard(() => step(-1))} aria-label={t.prev}><Prev size={16} /></button>
          <span style={{ fontWeight: 700, fontSize: 14, display: "inline-flex", gap: 8, alignItems: "center", padding: "0 8px", minWidth: 0 }} dir="auto">
            <CalendarDays size={16} style={{ color: styles.accent, flexShrink: 0 }} /> {rangeLabel}
          </span>
          <button className="sc-ghost" style={{ ...styles.btn("ghost"), padding: 9 }} onClick={guard(() => step(1))} aria-label={t.next}><Next size={16} /></button>
        </div>
        <button className="sc-ghost" style={styles.btn("ghost")} onClick={guard(() => setAnchor(todayKey()))}>{t.today}</button>
        <div style={{ flex: "0 1 220px", minWidth: 160 }}>
          <ReusableDropdown
            key={`company-${pickerReset}`}
            items={[{ key: "", label: t.allCompanies }, ...meta.companies.map((c) => ({ key: c, label: c }))]}
            value={company}
            onSelect={guardPick((item) => { setCompany(item.key); setDepartmentId(""); setPage(1); })}
            placeholder={t.allCompanies}
            getItemKey={(it) => it.key}
            getItemLabel={(it) => it.label}
            searchable={false}
            maxItems={100}
            theme={theme}
            language={language}
            ariaLabel={t.allCompanies}
          />
        </div>
        <div style={{ flex: "0 1 300px", minWidth: 180 }}>
          <ReusableDropdown
            key={`department-${pickerReset}`}
            items={[
              { key: "", label: t.allDepartments },
              ...departments.map((d) => ({
                key: String(d.id),
                label: `${(language === "ar" && d.nameAr) || d.name}${company ? "" : ` · ${d.workingCompany}`}`,
              })),
            ]}
            value={departmentId}
            onSelect={guardPick((item) => { setDepartmentId(item.key); setPage(1); })}
            placeholder={t.allDepartments}
            getItemKey={(it) => it.key}
            getItemLabel={(it) => it.label}
            maxItems={200}
            theme={theme}
            language={language}
            ariaLabel={t.allDepartments}
          />
        </div>
        <input className="sc-input" style={{ ...styles.input, flex: "1 1 200px" }} placeholder={t.search} value={searchInput}
          onChange={(e) => {
            const next = e.target.value;
            const apply = () => { setSearchInput(next); setPage(1); };
            if (dirty) confirmDiscard(apply);
            else apply();
          }} />
      </div>

      {/* bulk tools */}
      <div style={{ ...styles.toolbar, alignItems: "flex-end", gap: "12px 14px" }}>
        <div style={styles.countChip(selected.size > 0)}>
          <Wand2 size={16} />
          <span><b>{selected.size}</b> {t.selected}</span>
        </div>
        <div style={styles.field("0 1 250px", 180)}>
          <span style={styles.fieldLabel}>{t.bulkValue}</span>
          <ReusableDropdown
            items={[
              { key: "", label: t.clear },
              ...legendShifts.filter((s) => !company || s.workingCompany === company).map((s) => ({
                key: `S:${s.id}`,
                label: `${s.name} (${s.startTime}–${s.endTime})${company ? "" : ` · ${s.workingCompany}`}`,
              })),
              { key: "REST", label: t.rest },
            ]}
            value={bulk.value}
            onSelect={(item) => setBulk((b) => ({ ...b, value: item.key }))}
            placeholder={t.clear}
            getItemKey={(it) => it.key}
            getItemLabel={(it) => it.label}
            searchable={false}
            maxItems={100}
            theme={theme}
            language={language}
            ariaLabel={t.bulkValue}
          />
        </div>
        <div style={styles.field("0 1 210px", 180)}>
          <span style={styles.fieldLabel}>{t.from}</span>
          <DatePicker theme={theme} isRTL={language === "ar"} value={bulk.from || from} minDate={from} maxDate={to}
            onChange={(v) => setBulk((b) => ({ ...b, from: v }))} style={{ width: "100%" }} />
        </div>
        <div style={styles.field("0 1 210px", 180)}>
          <span style={styles.fieldLabel}>{t.to}</span>
          <DatePicker theme={theme} isRTL={language === "ar"} value={bulk.to || to} minDate={from} maxDate={to}
            onChange={(v) => setBulk((b) => ({ ...b, to: v }))} style={{ width: "100%" }} />
        </div>
        <label style={styles.checkRow}>
          <input type="checkbox" className="sc-check" style={styles.checkbox} checked={bulk.skipWeekends}
            onChange={(e) => setBulk((b) => ({ ...b, skipWeekends: e.target.checked }))} />
          {t.skipWeekends}
        </label>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginInlineStart: "auto" }}>
          {mode === "week" && (
            <button className="sc-ghost" style={styles.btn("ghost", dirty)} disabled={dirty}
              onClick={() => setConfirmCopy(selected.size ? [...selected] : pageCodes)}>
              <Copy size={15} /> {t.copyPrevWeek}
            </button>
          )}
          <button className="sc-primary" style={{ ...styles.btn("primary", !selected.size), padding: "10px 22px" }} disabled={!selected.size} onClick={applyBulk}>
            <Check size={16} /> {t.apply}
          </button>
        </div>
      </div>

      {/* legend */}
      <div style={styles.legendRow}>
        {shownLegend.map((s) => (
          <span key={s.id} style={styles.legendChip(s.color || "#3B82F6")}
            title={`${s.name} · ${s.startTime}–${s.endTime} · ${s.locationName}${company ? "" : ` · ${s.workingCompany}`}`}>
            <span style={styles.dot(s.color || "#3B82F6")} />
            <span style={styles.legendName} dir="auto">{s.name}</span>
            <span style={styles.legendSep} />
            <span style={styles.legendMeta}>
              <Clock size={12} />
              <span dir="ltr">{s.startTime}–{s.endTime}</span>
            </span>
          </span>
        ))}
        {hiddenLegend > 0 && (
          <button type="button" style={styles.legendMoreBtn} onClick={() => setLegendOpen((v) => !v)}>
            {legendOpen
              ? <><ChevronUp size={14} /> {t.legendLess}</>
              : <><ChevronDown size={14} /> {t.legendMore.replace("{n}", String(hiddenLegend))}</>}
          </button>
        )}
        <span style={{ ...styles.hint, marginInlineStart: "auto" }}>{t.pastNote}</span>
      </div>

      {/* grid */}
      {loading && !data ? (
        <div style={styles.emptyState}>{t.loading}</div>
      ) : !data?.employees?.length ? (
        <div style={styles.emptyState}>{t.noEmployees}</div>
      ) : (
        <div className="sc-grid" style={{ ...styles.gridScroll, opacity: loading ? 0.6 : 1, transition: "opacity 0.2s ease" }}>
          <table style={styles.gridTable}>
            <thead>
              <tr>
                <th style={{ ...styles.gridHeadCell(false, true), textAlign: language === "ar" ? "right" : "left", padding: "10px 12px" }}>
                  <label style={{ display: "inline-flex", gap: 8, alignItems: "center", cursor: "pointer" }} title={t.selectAll}>
                    <input type="checkbox" className="sc-check" style={styles.checkbox} checked={allSelected} onChange={toggleAll} />
                    {t.employee}
                  </label>
                </th>
                {days.map((d) => (
                  <th key={d} style={styles.gridHeadCell(d === today, false)}>
                    <div>{weekdayNames[dowOf(d)]}</div>
                    <div style={{ fontWeight: 600 }} dir="ltr">{d.slice(8, 10)}/{d.slice(5, 7)}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.employees.map((emp) => (
                <tr key={emp.infinityCode}>
                  <td style={styles.gridNameCell}>
                    <label style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer" }}>
                      <input type="checkbox" className="sc-check" checked={selected.has(emp.infinityCode)}
                        onChange={() => toggle(emp.infinityCode)} style={{ ...styles.checkbox, marginTop: 3, flexShrink: 0 }} />
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: "block", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {displayName(emp)}
                        </span>
                        <span style={{ display: "block", fontSize: 11.5, color: styles.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {emp.clientCode || emp.infinityCode}
                          {emp.department ? ` · ${emp.department.name}` : ""}
                          {!company && emp.workingCompany ? ` · ${emp.workingCompany}` : ""}
                        </span>
                        {activeShiftsFor(emp.workingCompany).length === 0 && (
                          <span style={{ display: "block", fontSize: 11, color: "#f59e0b" }}>{t.noShiftForCompany}</span>
                        )}
                      </span>
                    </label>
                  </td>
                  {days.map((d) => renderCell(emp, d))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* pagination */}
      {data && totalPages > 1 && (
        <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "center", marginTop: 10, flexShrink: 0 }}>
          <button className="sc-ghost" style={{ ...styles.btn("ghost", page <= 1), padding: 9 }} disabled={page <= 1} onClick={guard(() => setPage(page - 1))}><Prev size={16} /></button>
          <span style={{ fontSize: 13, color: styles.muted, fontWeight: 600 }}>{t.page} {page} {t.of} {totalPages}</span>
          <button className="sc-ghost" style={{ ...styles.btn("ghost", page >= totalPages), padding: 9 }} disabled={page >= totalPages} onClick={guard(() => setPage(page + 1))}><Next size={16} /></button>
        </div>
      )}

      {/* unsaved bar — rendered into the dialog footer */}
      {dirty && footerEl && createPortal(
        <>
          <span style={styles.unsavedNote}>
            <span style={styles.unsavedDot} />
            {edits.size} {t.unsaved}
          </span>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="sc-ghost" style={{ ...styles.btn("ghost", saving), padding: "10px 20px" }} disabled={saving} onClick={() => setEdits(new Map())}>
              <Undo2 size={15} /> {t.discard}
            </button>
            <button className="sc-primary" style={{ ...styles.btn("primary", saving), padding: "10px 24px" }} disabled={saving} onClick={save}>
              <Save size={15} /> {t.saveChanges}
            </button>
          </div>
        </>,
        footerEl,
      )}
    </div>
  );
}
